/**
 * Utility functions for reading tier limits from Firebase config
 * Falls back to defaults if config doesn't exist
 */

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

export interface TierLimits {
  formLimit: number | null;
  submissionLimit: number | null;
  exportLimit: number | null;
  recipientLimit: number | null;
  storageLimit: number;
  memberLimit: number | null;
}

export interface TierConfig {
  pro: TierLimits;
  proPlus: TierLimits;
  enterprise: TierLimits;
  free: TierLimits;
}

// Default limits (fallback if config doesn't exist)
const DEFAULT_TIER_CONFIG: TierConfig = {
  pro: {
    formLimit: 5,
    submissionLimit: 1000,
    exportLimit: 10,
    recipientLimit: 5,
    storageLimit: 100,
    memberLimit: 1,
  },
  proPlus: {
    formLimit: 15,
    submissionLimit: 5000,
    exportLimit: 50,
    recipientLimit: 15,
    storageLimit: 500,
    memberLimit: 10,
  },
  enterprise: {
    formLimit: null, // unlimited
    submissionLimit: null,
    exportLimit: null,
    recipientLimit: null,
    storageLimit: 1000,
    memberLimit: null, // unlimited
  },
  free: {
    formLimit: 1,
    submissionLimit: null,
    exportLimit: null,
    recipientLimit: 1,
    storageLimit: 10,
    memberLimit: 1, // just the owner
  }
};

let cachedConfig: TierConfig | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get tier limits from Firebase config (with caching)
 */
export const getTierLimits = async (tier: 'free' | 'pro' | 'proPlus' | 'enterprise'): Promise<TierLimits> => {
  // Check cache first
  const now = Date.now();
  if (cachedConfig && (now - cacheTimestamp) < CACHE_DURATION) {
    return cachedConfig[tier];
  }

  try {
    const configDoc = await getDoc(doc(db, 'system_config', 'tier_limits'));
    
    if (configDoc.exists()) {
      const data = configDoc.data() as TierConfig;
      cachedConfig = data;
      cacheTimestamp = now;
      return data[tier] || DEFAULT_TIER_CONFIG[tier];
    } else {
      // Config doesn't exist, use defaults
      cachedConfig = DEFAULT_TIER_CONFIG;
      cacheTimestamp = now;
      return DEFAULT_TIER_CONFIG[tier];
    }
  } catch (error) {
    console.error('Error loading tier config:', error);
    // Return defaults on error
    return DEFAULT_TIER_CONFIG[tier];
  }
};

/**
 * Get all tier config (for admin use)
 */
export const getAllTierConfig = async (): Promise<TierConfig> => {
  // Check cache first
  const now = Date.now();
  if (cachedConfig && (now - cacheTimestamp) < CACHE_DURATION) {
    return cachedConfig;
  }

  try {
    const configDoc = await getDoc(doc(db, 'system_config', 'tier_limits'));
    
    if (configDoc.exists()) {
      const data = configDoc.data() as TierConfig;
      cachedConfig = data;
      cacheTimestamp = now;
      return data;
    } else {
      cachedConfig = DEFAULT_TIER_CONFIG;
      cacheTimestamp = now;
      return DEFAULT_TIER_CONFIG;
    }
  } catch (error) {
    console.error('Error loading tier config:', error);
    return DEFAULT_TIER_CONFIG;
  }
};

/**
 * Clear the cache (call after updating tier config)
 */
export const clearTierConfigCache = () => {
  cachedConfig = null;
  cacheTimestamp = 0;
};

/**
 * Get default tier config (for reference)
 */
export const getDefaultTierConfig = (): TierConfig => {
  return DEFAULT_TIER_CONFIG;
};
